import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatestWith, switchMap } from 'rxjs/operators';
import * as fromActions from './actions';
import { Store } from '@ngrx/store';
import { OrganizationVehicleService, VehiclePositionDto, VehicleStatus } from '@services/api/api.service';
import { promiseWrapper } from '@shared/_funtions/promiseWarapper.function';
import { loadedOrganizationsSubscriber, selectSelectedOrganizationId, selectUser } from '@redux/index';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env';
import { DataTraffic } from '@models/tractor';

@Injectable()
export class TractorDetailCustomerEffects {
  private organizationId: string;
  private dataTraffic: DataTraffic;
  private vin: string;
  private isPollingActive = false;
  public getTractorDetail$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getTractorDetailCustomer),
    combineLatestWith(
      this.store.select(selectUser),
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    switchMap(async ([action, user, selectedOrganizationId]) => {
      this.store.dispatch(fromActions.setTractorDetailLoadingCustomer({ value: true }));

      try {
        if (!selectedOrganizationId) {
          return [fromActions.setTractorDetailLoadingCustomer({ value: false })];
        }

        this.organizationId = selectedOrganizationId;
        const vin = action.vin;
        const language = user?.user_metadata?.language || 'en';

        // Load all vehicle data in parallel
        let vehicleParams, vehicleAlarms, vehicleWarranty, vehicleMaintenance, geoviewResponse;
        try {
          vehicleParams = await promiseWrapper(this.organizationVehicleService.getOrganizationVehicleLastParam(this.organizationId, vin));
        } catch (e) {
          console.log(e);
        }
        try {
          vehicleAlarms = await promiseWrapper(this.organizationVehicleService.getVehicleAlarm(this.organizationId, vin, language));
        } catch (e) {
          console.log(e);
        }
        try {
          vehicleWarranty = await promiseWrapper(this.organizationVehicleService.getVehicleWarranty(this.organizationId, vin));
        } catch (e) {
          console.log(e);
        }
        try {
          vehicleMaintenance = await promiseWrapper(this.organizationVehicleService.getOrganizationVehicleMaintenanceStatus(this.organizationId, vin, language));
        } catch (e) {
          console.log(e);
        }
        try {
          geoviewResponse = await promiseWrapper(this.organizationVehicleService.getVehiclesStatusGeoview(
            this.organizationId,
            vin,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
          ));
        } catch (e) {
          console.log(e);
        }

        // Create vehicle object from geoview data
        const selected = geoviewResponse?.vehicles?.find(v => v.vin === vin) || geoviewResponse?.vehicles?.[0];
        const vehicle = {
          vin,
          name: selected?.name,
          model: selected?.model,
          dataTraffic: selected?.dataTraffic || 'SDF', // Default to SDF if not specified
        };

        // Derive position and weather using organization data only
        let vehiclePosition = null;
        let meteo = null as any;
        if (selected?.latitude != null && selected?.longitude != null) {
          vehiclePosition = {
            latitude: selected.latitude,
            longitude: selected.longitude,
            messageReceivedDateTime: selected.lastPositionUpdate,
          } as any;
        }
        if (vehiclePosition) {
          meteo = await this.getMeteo(vehiclePosition.latitude, vehiclePosition.longitude);
        }

        // Set up WebSocket connection properties
        this.vin = vin;
        const dataTraffic = DataTraffic[vehicle.dataTraffic as keyof typeof DataTraffic];
        this.dataTraffic = dataTraffic;

        return [
          fromActions.setTractorDetailCustomer({
            vehicle,
            vehicleAlarmsActive: vehicleAlarms || [],
            vehicleMaintenance: null,
            vehicleDTechInfo: null,
            ctmSerial: null,
            vehicleOwner: null,
            vehicleWarranty,
            alarmHistoryCounter: vehicleAlarms?.length || 0,
            sbd: null,
            hasDealer: null,
          }),
          fromActions.setTractorMaintenanceStatusCustomer({ vehicleMaintenanceStatus: vehicleMaintenance }),
          fromActions.setTractorsDetailSummaryCustomer({ data: vehicleParams, vehiclePosition, meteo }),
          fromActions.setTractorDetailLoadingCustomer({ value: false }),
          // Start WebSocket connection if not in noPolling mode
          ...(!action.noPolling ? [fromActions.startPollingTractorDetailCustomer({ vin, dataTraffic })] : []),
        ];
      } catch (error) {
        console.error('Error loading customer vehicle details:', error);
        return [fromActions.setTractorDetailLoadingCustomer({ value: false })];
      }
    }),
    switchMap((actions) => [...actions]),
  ));

  public getTractorMaintenance$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getTractorMaintenanceCustomer),
    combineLatestWith(
      this.store.select(selectUser),
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    switchMap(async ([action, user, selectedOrganizationId]) => {
      try {
        if (!selectedOrganizationId) {
          return fromActions.setTractorMaintenanceStatusCustomer({ vehicleMaintenanceStatus: null });
        }

        this.organizationId = selectedOrganizationId;
        const vin = action.vin;
        const language = user?.user_metadata?.language || 'en';

        const vehicleMaintenance = await promiseWrapper(
          this.organizationVehicleService.getOrganizationVehicleMaintenanceStatus(this.organizationId, vin, language),
        );

        return fromActions.setTractorMaintenanceStatusCustomer({ vehicleMaintenanceStatus: vehicleMaintenance });
      } catch (error) {
        console.error('Error loading customer vehicle maintenance:', error);
        return fromActions.setTractorMaintenanceStatusCustomer({ vehicleMaintenanceStatus: null });
      }
    }),
  ));
  public getTractorMaintenanceHistory$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getTractorMaintenanceHistoryCustomer),
    combineLatestWith(
      this.store.select(selectUser),
      this.store.select(selectSelectedOrganizationId),
      loadedOrganizationsSubscriber(this.store),
    ),
    switchMap(async ([action, user, selectedOrganizationId]) => {
      try {
        // Get organization ID first
        if (!selectedOrganizationId) {
          return fromActions.setTractorMaintenanceHistoryCustomer({ vehicleMaintenanceHistory: [] });
        }

        this.organizationId = selectedOrganizationId;
        const vin = action.vin;
        const language = user?.user_metadata?.language || 'en';

        const vehicleMaintenanceHistory = await promiseWrapper(
          this.organizationVehicleService.getOrganizationVehicleMaintenanceHistory(this.organizationId, vin, language),
        );

        return fromActions.setTractorMaintenanceHistoryCustomer({ vehicleMaintenanceHistory: vehicleMaintenanceHistory || [] });
      } catch (error) {
        console.error('Error loading customer vehicle maintenance history:', error);
        return fromActions.setTractorMaintenanceHistoryCustomer({ vehicleMaintenanceHistory: [] });
      }
    }),
  ));
  private pollingInterval: any;
  private lastPosition: any;
  private hubConnection: HubConnection;
  public startPolling$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.startPollingTractorDetailCustomer),
    switchMap((action: any) => {
      this.isPollingActive = true;
      this.vin = action.vin;
      this.dataTraffic = action.dataTraffic;
      console.log('startPollingTractorDetailCustomer', { vin: this.vin, dataTraffic: this.dataTraffic });
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
      this.pollingInterval = setInterval(() => {
        if (this.isPollingActive && this.lastPosition) {
          this.syncData();
        }
      }, 30000); // 30 seconds
      return [fromActions.obtainTractorDetailJwtCustomer()];
    }),
    switchMap(async (actions) => actions),
  ));
  public stopPolling$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.stopPollingTractorDetailCustomer),
    switchMap(() => {
      this.isPollingActive = false;
      const vin = this.vin;
      this.vin = undefined;
      this.dataTraffic = undefined;
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      this.lastPosition = null;
      if (this.hubConnection) {
        this.hubConnection.invoke('LeaveVinGroup', { vin }).then(() => {
          this.hubConnection.stop();
        }).catch((e) => {
          console.error('Error stopping hub connection:', e);
          this.hubConnection.stop().catch(() => {});
        });
      }
      return [fromActions.setTractorsDetailSummaryCustomer({ vehiclePosition: undefined, meteo: undefined, data: undefined })];
    }),
  ));
  private telemetriesUrls = {
    1: environment.btmWsUrl + '/hubs/telemetry',
    2: environment.sdfWsUrl + '/hubs/telemetry',
  };
  public obtainTractorDetailJwt$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.obtainTractorDetailJwtCustomer),
    switchMap(async () => {
      const accessToken = await promiseWrapper(this.auth.getAccessTokenSilently());
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${this.telemetriesUrls[this.dataTraffic]}?access_token=${accessToken}`)
        .withAutomaticReconnect()
        .build();
      this.hubConnection.on('ReceivedMessage', (tractorData) => {
        if (this.vin !== tractorData.vin) {
          return;
        }
        const telemetries: any = {};
        tractorData.telemetries.forEach((item) => {
          telemetries[item.category] = item.physicalValue;
        });
        if (telemetries.engineSpeed != null && telemetries.engineSpeed < 600) {
          telemetries.vehicleStatus = VehicleStatus.NotRunning;
        }
        telemetries.paramsDateTime = tractorData.timeStamp;
        this.lastPosition = {
          latitude: tractorData.latitude,
          longitude: tractorData.longitude,
          messageReceivedDateTime: tractorData.timeStamp,
          altitude: tractorData.altitude,
          rtkGnssQuality: telemetries.rtkGnssQuality != null
            ? String(telemetries.rtkGnssQuality)
            : null,
        };
        this.store.dispatch(fromActions.setTractorsDetailSummaryCustomer({ data: telemetries, vehiclePosition: this.lastPosition }));
      });
      const startConnection = async () => {
        try {
          await this.hubConnection.start().then(() => this.hubConnection.invoke('JoinVinGroup', {
            vin: this.vin,
          }));
        } catch (err) {
          setTimeout(startConnection, 5000);
        }
      };
      this.hubConnection.onclose(() => {
        if (this.isPollingActive) {
          startConnection();
        }
      });
      startConnection();
      return [];
    }),
  ), { dispatch: false });

  private async getMeteo(latitude: number, longitude: number) {
    try {
      const data: any = await promiseWrapper(this.http.get(
        `https://api.open-meteo.com/v1/forecast`, {
          params: {
            longitude,
            latitude,
            current: ['temperature_2m', 'weather_code', 'relative_humidity_2m', 'wind_speed_10m'].join(','),
            timezone: 'Europe/Berlin',
          },
        }));
      return data?.current ? {
        temperature_2m: data.current.temperature_2m ?? null,
        time: data.current.time ?? null,
        weather_code: data.current.weather_code ?? null,
        relative_humidity_2m: data.current.relative_humidity_2m ?? null,
        wind_direction_10m: data.current.wind_direction_10m ?? null,
        wind_speed_10m: data.current.wind_speed_10m ?? null,
        precipitation: data.current.precipitation ?? '',
        monthly_precipitations: '0.0',
        interval: 0,
      } : null;
    } catch (e) {
      // ignore meteo failures
      return null;
    }
  }

  private async syncData() {
    if (this.lastPosition) {
      const meteo = await this.getMeteo(this.lastPosition.latitude, this.lastPosition.longitude);
      this.store.dispatch(fromActions.setTractorsDetailSummaryCustomer({ meteo }));
    }
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private organizationVehicleService: OrganizationVehicleService,
    private http: HttpClient,
    private auth: AuthService,
  ) {
  }
}
